import React from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router"

import { Page404 } from "@/components/nav/page404"
import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import { useActiveOrganizationId } from "@/contexts"
import { AdminEntityFieldsSkeleton } from "@/modules/admin/components/admin-entity-fields-skeleton"
import { useAdminCredentialPageState } from "@/modules/admin/credentials"
import { useUrl } from "@/utils/url"

import { Link as MuiLink } from "@mui/material"
import Button from "@mui/material/Button"
import Stack from "@mui/material/Stack"

import { AdminCredentialEditForm } from "./admin-credential-edit-form"

export const AdminCredentialEditRoute: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation("common")
  const { activeOrganizationId } = useActiveOrganizationId()
  const { adminCredentialPath, adminCredentialsPath } = useUrl()
  const {
    credentialData,
    credentialError,
    credentialId,
    credentialIsLoading,
    credentialIsUnavailable,
  } = useAdminCredentialPageState()

  const organizationId = activeOrganizationId ?? ""

  if (!organizationId) {
    return null
  }

  if (credentialError && !credentialData) {
    throw credentialError
  }

  if (credentialIsUnavailable) {
    return <Page404 />
  }

  if (credentialIsLoading) {
    return (
      <Stack sx={{ gap: 2 }}>
        <PageTitle
          breadcrumbActions={
            <>
              <MuiLink
                component={Link}
                to={adminCredentialsPath}
                underline="hover"
              >
                {t("admin.credentials")}
              </MuiLink>
              {credentialId !== undefined ? (
                <MuiLink
                  component={Link}
                  to={adminCredentialPath(credentialId)}
                  underline="hover"
                >
                  {t("admin.credentialDetail.viewCredential")}
                </MuiLink>
              ) : null}
            </>
          }
          heading={t("admin.credentialsUi.editDialog.title")}
          primaryActions={
            credentialId === undefined ? undefined : (
              <Button
                onClick={() => {
                  void navigate(adminCredentialPath(credentialId), {
                    replace: true,
                  })
                }}
                variant="outlined"
              >
                {t("button.cancel")}
              </Button>
            )
          }
        />
        <PageCard>
          <Stack sx={{ pb: 1 }}>
            <AdminEntityFieldsSkeleton
              ariaLabel={t("loadingAriaLabel", {
                title: t("admin.credentialDetail.title"),
              })}
              roundedInputs
            />
          </Stack>
        </PageCard>
      </Stack>
    )
  }

  if (!credentialData || credentialId === undefined) {
    return null
  }

  return (
    <AdminCredentialEditForm
      key={credentialId}
      credential={credentialData}
      organizationId={organizationId}
    />
  )
}
